import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useStudioStore } from '@/store/useStudioStore';
import { useExpertStore } from '@/store/useExpertStore';
import { StudioBuildStage } from './StudioBuildStage';
import { StudioPreviewSkeleton } from './StudioPreviewSkeleton';
import {
  useCreatorStore,
  type SelectedElementInfo,
  type SelectedElementNode,
  type SelectedElementValueTarget,
} from '@/store/useCreatorStore';
// @ts-expect-error: linker-parent is a generated JS file without declaration
import { createLinkerHost, MessageType } from '@/lib/linker-parent.es.femf3n5t.js';
import { isSpecialProjectType } from '@/lib/projectUtils.ts';
import { trackEvent } from '@/api/track';
import { projectApi } from '@/api/project';
import { toast } from 'sonner';
import { isEditableNavigationHref } from '@/lib/hrefUtils';
import {
  rememberRecentDraftStatus,
  resolveEditableDraft,
  saveConfirmedDraftSnapshot,
} from '@/lib/draftUtils';
import { buildDomTargetRef, isDomTargetRef, resolveDomTargetRef } from '@/lib/domTargetRef';
import { normalizeLegacyPlatformUrlWithTrailingSlash } from '@/lib/platformDomain';
import {
  buildPermanentPreviewHtml,
  buildDraftPreviewHtml,
  removeLegacyLandingPageTrackingScriptsFromDocument,
  removeLegacyGoogleAdsTrackingScriptsFromDocument,
  stripLegacyLandingPageTrackingScript,
  stripLegacyGoogleAdsTrackingScript,
} from '@/lib/domUtils';
import {
  removeStudioGoogleAdsTrackingScriptsFromDocument,
  STUDIO_PREVIEW_WINDOW_NAME,
  stripStudioGoogleAdsTrackingScript,
} from '@/lib/studioGoogleAds';
import {
  removeStudioAnalyticsTrackingScriptsFromDocument,
  stripStudioAnalyticsTrackingScript,
} from '@/lib/studioAnalytics';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import { loadPermanentPreviewHtml } from '@/lib/previewUtils';

const DRAFT_REVIEW_RESOLUTION_TOAST_ID = 'draft-review-resolution';
const PREVIEW_DRAFT_RECOVERY_MAX_ATTEMPTS = 30;
const PREVIEW_DRAFT_RECOVERY_INTERVAL_MS = 1000;

type PreviewRecoveryStatus = 'idle' | 'recovering' | 'timed_out';

const injectLinkInterceptor = (html: string): string => {
  const script = `<script>(function(){
  document.addEventListener('click', function(e){
    var a = e.target.closest('a');
    if(!a) return;
    var href = a.getAttribute('href');
    if(!href) return;
    if(href.startsWith('#')){
      e.preventDefault();
      var el = document.getElementById(href.slice(1));
      if(el) el.scrollIntoView({behavior:'smooth'});
      return;
    }
    try {
      var url = new URL(href, location.href);
      if(url.origin !== location.origin){ e.preventDefault(); }
    } catch(_){ e.preventDefault(); }
  }, true);
})();</script>`;
  if (html.includes('</body>')) return html.replace('</body>', script + '</body>');
  if (html.includes('</html>')) return html.replace('</html>', script + '</html>');
  return html + script;
};

const getHtmlContentKey = (html: string) => {
  let hash = 0;
  for (let index = 0; index < html.length; index += 1) {
    hash = (hash * 31 + html.charCodeAt(index)) >>> 0;
  }
  return `iframe-srcdoc-${html.length}-${hash}`;
};

const waitForDraftSync = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

type PreviewScrollPosition = {
  top: number;
  left: number;
  topRatio: number;
  leftRatio: number;
};

type PreviewScrollBucket = 'default' | 'before' | 'after';

export type PointerSelectionHint = {
  target: HTMLElement | null;
  clientX: number;
  clientY: number;
};

export type DirectSelectionLock = {
  target: HTMLElement;
  selectionRoot: HTMLElement;
  payload: LinkerSelectedPayload;
  expiresAt: number;
};

const clonePreviewScrollPosition = (position: PreviewScrollPosition): PreviewScrollPosition => ({
  ...position,
});

const getPreviewScrollBucket = (
  reviewActive: boolean,
  reviewView: 'before' | 'after',
): PreviewScrollBucket => (reviewActive ? reviewView : 'default');

const getPreviewScrollPosition = (
  iframeElement: HTMLIFrameElement | null,
): PreviewScrollPosition | null => {
  try {
    const iframeWindow = iframeElement?.contentWindow;
    const iframeDocument = iframeElement?.contentDocument || iframeWindow?.document;
    const scrollingElement =
      iframeDocument?.scrollingElement || iframeDocument?.documentElement || iframeDocument?.body;
    if (!iframeWindow || !scrollingElement) {
      return null;
    }

    const top = Math.max(
      iframeWindow.scrollY || 0,
      scrollingElement.scrollTop || 0,
      iframeDocument?.documentElement?.scrollTop || 0,
      iframeDocument?.body?.scrollTop || 0,
    );
    const left = Math.max(
      iframeWindow.scrollX || 0,
      scrollingElement.scrollLeft || 0,
      iframeDocument?.documentElement?.scrollLeft || 0,
      iframeDocument?.body?.scrollLeft || 0,
    );
    const maxTop = Math.max(0, scrollingElement.scrollHeight - iframeWindow.innerHeight);
    const maxLeft = Math.max(0, scrollingElement.scrollWidth - iframeWindow.innerWidth);

    return {
      top,
      left,
      topRatio: maxTop > 0 ? top / maxTop : 0,
      leftRatio: maxLeft > 0 ? left / maxLeft : 0,
    };
  } catch {
    return null;
  }
};

const restorePreviewScrollPosition = (
  iframeElement: HTMLIFrameElement | null,
  position: PreviewScrollPosition | null,
) => {
  if (!position) return;

  try {
    const iframeWindow = iframeElement?.contentWindow;
    const iframeDocument = iframeElement?.contentDocument || iframeWindow?.document;
    const scrollingElement =
      iframeDocument?.scrollingElement || iframeDocument?.documentElement || iframeDocument?.body;
    if (!iframeWindow || !scrollingElement) {
      return;
    }

    const maxTop = Math.max(0, scrollingElement.scrollHeight - iframeWindow.innerHeight);
    const maxLeft = Math.max(0, scrollingElement.scrollWidth - iframeWindow.innerWidth);
    const nextTop =
      position.top <= maxTop ? position.top : Math.max(0, Math.round(position.topRatio * maxTop));
    const nextLeft =
      position.left <= maxLeft
        ? position.left
        : Math.max(0, Math.round(position.leftRatio * maxLeft));

    iframeWindow.scrollTo(nextLeft, nextTop);
    if (scrollingElement.scrollTop !== nextTop) {
      scrollingElement.scrollTop = nextTop;
    }
    if (scrollingElement.scrollLeft !== nextLeft) {
      scrollingElement.scrollLeft = nextLeft;
    }
  } catch {
    // Ignore cross-origin iframe access failures.
  }
};

export type LinkerSelectedPayload = {
  id: string;
  text: string;
  nodeName: string;
  src?: string;
  href?: string;
};

const LINKER_SELECTED_CLASS = 'linker-selected';
const MANUAL_SELECTION_TARGET_ATTR = 'data-manual-selection-target';
const MANUAL_SELECTION_HOVER_ATTR = 'data-manual-selection-hover';
const MANUAL_SELECTION_SUPPRESSED_ATTR = 'data-manual-selection-suppressed';
const DIRECT_SELECTION_LOCK_MS = 1500;

const isElementNode = (value: unknown): value is Element =>
  Boolean(value && typeof value === 'object' && (value as Node).nodeType === Node.ELEMENT_NODE);

const isTextNode = (value: unknown): value is Text =>
  Boolean(value && typeof value === 'object' && (value as Node).nodeType === Node.TEXT_NODE);

export const asSelectionElement = (value: unknown) =>
  (isElementNode(value)
    ? value
    : isTextNode(value)
      ? value.parentElement
      : null) as HTMLElement | null;

const normalizeTextValue = (value?: string | null) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const FORM_CONTROL_SELECTOR = 'input,textarea,select,option';
const FORM_CONTROL_NODE_NAMES = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);
const TEXT_BLOCK_SELECTION_SELECTOR =
  'p,h1,h2,h3,h4,h5,h6,blockquote,figcaption,li,dt,dd,legend,button,a,label';

const INLINE_TEXT_WRAPPER_TAGS = new Set([
  'SPAN',
  'STRONG',
  'EM',
  'B',
  'I',
  'SMALL',
  'MARK',
  'TIME',
  'SUP',
  'SUB',
  'U',
  'S',
]);

const getDirectFormControlValue = (element: Element | null) => {
  if (!element) return '';

  const tagName = element.tagName.toUpperCase();

  if (tagName === 'INPUT') {
    // Input value is runtime user-entered state, not stable editable copy for selection.
    return '';
  }

  if (tagName === 'TEXTAREA') {
    return normalizeTextValue((element as HTMLTextAreaElement).value);
  }

  if (tagName === 'SELECT') {
    const selectElement = element as HTMLSelectElement;
    const selectedOptions = Array.from(selectElement.selectedOptions || []);
    const optionText = selectedOptions
      .map((option) => normalizeTextValue(option.textContent))
      .join(' ');
    return optionText || normalizeTextValue(selectElement.value);
  }

  if (tagName === 'OPTION') {
    const optionElement = element as HTMLOptionElement;
    return normalizeTextValue(optionElement.textContent) || normalizeTextValue(optionElement.value);
  }

  return '';
};

const getElementTextContent = (element: Element | null) =>
  getDirectFormControlValue(element) || normalizeTextValue(element?.textContent);

const getElementClassName = (element: Element | null) => {
  const className = normalizeTextValue(element?.getAttribute('class'));
  return className || undefined;
};

const hasStructuralChildElements = (element: HTMLElement) =>
  Array.from(element.children).some(
    (child) => !['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'].includes(child.tagName),
  );

const isDirectMediaNodeName = (nodeName?: string | null) =>
  ['IMG', 'IFRAME', 'VIDEO', 'SOURCE'].includes(String(nodeName || '').toUpperCase());

const getMediaSource = (element: Element | null) => {
  if (!element) return undefined;
  const mediaElement =
    element.tagName.toUpperCase() === 'SOURCE'
      ? element.parentElement
      : (element as HTMLElement | null);
  if (!mediaElement) return undefined;

  const mediaNodeWithCurrentSrc =
    'currentSrc' in mediaElement ? (mediaElement as { currentSrc?: string }) : null;

  return (
    mediaNodeWithCurrentSrc?.currentSrc?.trim() ||
    mediaElement.getAttribute('src')?.trim() ||
    mediaElement.getAttribute('srcset')?.split(',')[0]?.trim().split(/\s+/)[0]
  );
};

const getRectIntersectionArea = (a: DOMRect, b: DOMRect) => {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
};

const isVisibleMediaElement = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  if (rect.width < 24 || rect.height < 24) return false;

  const view = element.ownerDocument.defaultView;
  const style = view?.getComputedStyle(element);
  if (!style) return true;

  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (Number(style.opacity || '1') <= 0.01) return false;

  return true;
};

const findRelevantDescendantMedia = (element: HTMLElement) => {
  const rootRect = element.getBoundingClientRect();
  const rootArea = rootRect.width * rootRect.height;
  if (rootArea <= 0) return undefined;

  const candidates = Array.from(element.querySelectorAll('img,iframe,video')) as HTMLElement[];
  let bestCandidate:
    | {
        element: HTMLElement;
        src: string;
        visibleArea: number;
      }
    | undefined;

  for (const candidate of candidates) {
    if (!isVisibleMediaElement(candidate)) continue;

    const src = getMediaSource(candidate);
    if (!src) continue;

    const candidateRect = candidate.getBoundingClientRect();
    const visibleArea = getRectIntersectionArea(rootRect, candidateRect);
    const candidateArea = candidateRect.width * candidateRect.height;
    if (visibleArea <= 0 || candidateArea <= 0) continue;

    const overlapRatio = visibleArea / candidateArea;
    if (overlapRatio < 0.6) continue;

    if (!bestCandidate || visibleArea > bestCandidate.visibleArea) {
      bestCandidate = { element: candidate, src, visibleArea };
    }
  }

  return bestCandidate;
};

const findMatchingMediaElement = (rootElement: HTMLElement, src?: string) => {
  const normalizedSrc = normalizeTextValue(src);
  if (!normalizedSrc) return null;

  if (
    rootElement.matches('img,iframe,video,source') &&
    normalizeTextValue(getMediaSource(rootElement)) === normalizedSrc
  ) {
    return rootElement;
  }

  return (
    (Array.from(rootElement.querySelectorAll('img,iframe,video,source')) as HTMLElement[]).find(
      (element) => normalizeTextValue(getMediaSource(element)) === normalizedSrc,
    ) || null
  );
};

const findMatchingLinkElement = (rootElement: HTMLElement, href?: string) => {
  const normalizedHref = normalizeTextValue(href);
  if (!normalizedHref) return null;

  if (
    rootElement.matches('a[href]') &&
    normalizeTextValue(rootElement.getAttribute('href')) === normalizedHref
  ) {
    return rootElement;
  }

  return (
    (Array.from(rootElement.querySelectorAll('a[href]')) as HTMLAnchorElement[]).find(
      (element) => normalizeTextValue(element.getAttribute('href')) === normalizedHref,
    ) || null
  );
};

const getSelectionCandidateDepth = (rootElement: HTMLElement, candidate: HTMLElement) => {
  let depth = 0;
  let current: HTMLElement | null = candidate;

  while (current && current !== rootElement) {
    depth += 1;
    current = current.parentElement;
  }

  return depth;
};

const getSimilarSiblingCount = (element: HTMLElement) => {
  const parent = element.parentElement;
  if (!parent) return 1;

  const primaryClassName = getElementClassName(element)?.split(' ')[0];
  const siblings = Array.from(parent.children) as HTMLElement[];
  const matchingSiblings = siblings.filter((sibling) => {
    if (sibling.tagName !== element.tagName) return false;
    if (!primaryClassName) return true;
    return sibling.classList.contains(primaryClassName);
  });

  return matchingSiblings.length || 1;
};

const isMeaningfulSelectionScope = (element: HTMLElement, rootElement: HTMLElement) => {
  if (element === rootElement) return true;
  if (element.matches('img,iframe,video,source,a[href],button,label,svg')) return true;

  const hasIdentifier = Boolean(element.getAttribute('id')?.trim() || getElementClassName(element));
  const similarSiblingCount = getSimilarSiblingCount(element);

  if (hasIdentifier && (hasStructuralChildElements(element) || similarSiblingCount >= 2)) {
    return true;
  }

  if (similarSiblingCount >= 2 && Boolean(getElementTextContent(element))) {
    return true;
  }

  return false;
};

const findSelectionScopeFromMarkedElement = (
  rootElement: HTMLElement,
  markedElement: HTMLElement | null,
) => {
  if (!markedElement || !rootElement.contains(markedElement)) {
    return null;
  }

  let current: HTMLElement | null = markedElement;
  while (current && rootElement.contains(current)) {
    if (isMeaningfulSelectionScope(current, rootElement)) {
      return current;
    }

    if (current === rootElement) {
      break;
    }
    current = current.parentElement;
  }

  return markedElement;
};

const getSelectionCandidateScore = (
  rootElement: HTMLElement,
  candidate: HTMLElement,
  payload: LinkerSelectedPayload,
) => {
  if (
    ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'].includes(candidate.tagName) ||
    candidate === rootElement
  ) {
    return Number.NEGATIVE_INFINITY;
  }

  const payloadText = normalizeTextValue(payload.text);
  const candidateText = getElementTextContent(candidate);
  const normalizedHref = normalizeTextValue(payload.href);
  const normalizedSrc = normalizeTextValue(payload.src);
  const hasOwnMatchingHref =
    Boolean(normalizedHref) &&
    candidate.matches('a[href]') &&
    normalizeTextValue(candidate.getAttribute('href')) === normalizedHref;
  const hasDescendantMatchingHref =
    Boolean(normalizedHref) &&
    !hasOwnMatchingHref &&
    Boolean(findMatchingLinkElement(candidate, normalizedHref));
  const hasOwnMatchingSrc =
    Boolean(normalizedSrc) &&
    candidate.matches('img,iframe,video,source') &&
    normalizeTextValue(getMediaSource(candidate)) === normalizedSrc;
  const hasDescendantMatchingSrc =
    Boolean(normalizedSrc) &&
    !hasOwnMatchingSrc &&
    Boolean(findMatchingMediaElement(candidate, normalizedSrc));

  const hasTextMatch = Boolean(
    payloadText &&
    candidateText &&
    (candidateText === payloadText ||
      candidateText.includes(payloadText) ||
      payloadText.includes(candidateText)),
  );

  if (
    !hasTextMatch &&
    !hasOwnMatchingHref &&
    !hasDescendantMatchingHref &&
    !hasOwnMatchingSrc &&
    !hasDescendantMatchingSrc
  ) {
    return Number.NEGATIVE_INFINITY;
  }

  let score = 0;

  if (payloadText && candidateText) {
    if (candidateText === payloadText) {
      score += 120;
    } else if (candidateText.includes(payloadText)) {
      score += 88;
      score -= Math.min(28, Math.floor((candidateText.length - payloadText.length) / 10));
    } else if (payloadText.includes(candidateText)) {
      score += 28;
      score -= Math.min(16, Math.floor((payloadText.length - candidateText.length) / 6));
    }

    if (candidateText.length > payloadText.length + 180) {
      score -= 36;
    } else if (candidateText.length > Math.max(payloadText.length * 4, payloadText.length + 80)) {
      score -= 18;
    }
  }

  if (hasOwnMatchingHref) {
    score += 120;
  } else if (hasDescendantMatchingHref) {
    score += 24;
  }

  if (hasOwnMatchingSrc) {
    score += 120;
  } else if (hasDescendantMatchingSrc) {
    score += 24;
  }

  const childElementCount = candidate.children.length;
  const similarSiblingCount = getSimilarSiblingCount(candidate);
  const depth = getSelectionCandidateDepth(rootElement, candidate);
  const hasStructuralChildren = hasStructuralChildElements(candidate);

  if (hasStructuralChildren) {
    score += 24;
    score += Math.min(16, childElementCount * 4);
  } else {
    score -= 34;
  }

  if (similarSiblingCount >= 2) {
    score += 40;
    score += Math.min(16, (similarSiblingCount - 2) * 4);
  }

  if (getElementClassName(candidate)) {
    score += 12;
  }

  if (candidate.getAttribute('id')?.trim()) {
    score += 18;
  }

  score += Math.min(18, depth * 3);

  if (['DIV', 'LI', 'ARTICLE', 'SECTION', 'FIGURE', 'A', 'BUTTON'].includes(candidate.tagName)) {
    score += 8;
  }

  if (
    ['SPAN', 'STRONG', 'EM', 'B', 'I', 'SMALL', 'MARK', 'TIME'].includes(candidate.tagName) &&
    !hasOwnMatchingHref &&
    !hasOwnMatchingSrc
  ) {
    score -= 12;
  }

  return score;
};

const findBestSelectionScope = (
  rootElement: HTMLElement,
  payload: LinkerSelectedPayload,
  markedElement?: HTMLElement | null,
) => {
  const markedScope = findSelectionScopeFromMarkedElement(rootElement, markedElement || null);
  if (markedScope && markedScope !== rootElement) {
    return markedScope;
  }

  if (isDirectMediaNodeName(payload.nodeName)) {
    return findMatchingMediaElement(rootElement, payload.src) || rootElement;
  }

  if (String(payload.nodeName || '').toUpperCase() === 'A') {
    return findMatchingLinkElement(rootElement, payload.href) || rootElement;
  }

  const rootText = getElementTextContent(rootElement);
  const payloadText = normalizeTextValue(payload.text);

  if (payloadText && rootText && rootText === payloadText) {
    return rootElement;
  }

  const candidates = Array.from(rootElement.querySelectorAll('*')) as HTMLElement[];
  let bestCandidate: HTMLElement | null = null;
  let bestScore = 64;

  candidates.forEach((candidate) => {
    const score = getSelectionCandidateScore(rootElement, candidate, payload);
    if (score > bestScore) {
      bestCandidate = candidate;
      bestScore = score;
    }
  });

  return bestCandidate || rootElement;
};

const isPointInsideRect = (rect: DOMRect, clientX: number, clientY: number) =>
  clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;

const findPreferredDirectMediaTarget = (
  rootElement: HTMLElement,
  payload?: LinkerSelectedPayload,
  preferredHint?: PointerSelectionHint | null,
) => {
  const preferredMatchingMedia = findMatchingMediaElement(rootElement, payload?.src);
  if (preferredMatchingMedia) {
    return preferredMatchingMedia;
  }

  const preferredTarget = preferredHint?.target || null;
  let current: HTMLElement | null = preferredTarget || null;

  while (current && rootElement.contains(current)) {
    if (current.matches('img,iframe,video,source')) {
      return current;
    }

    if (current === rootElement) {
      break;
    }

    current = current.parentElement;
  }

  if (preferredHint) {
    const elementsFromPoint = rootElement.ownerDocument.elementsFromPoint?.(
      preferredHint.clientX,
      preferredHint.clientY,
    );

    if (elementsFromPoint?.length) {
      for (const element of elementsFromPoint) {
        let candidate: HTMLElement | null = element as HTMLElement | null;

        while (candidate && rootElement.contains(candidate)) {
          if (candidate.matches('img,iframe,video,source') && isVisibleMediaElement(candidate)) {
            return candidate;
          }

          if (candidate === rootElement) {
            break;
          }

          candidate = candidate.parentElement;
        }
      }
    }

    const mediaCandidates = [
      ...(rootElement.matches('img,iframe,video,source') ? [rootElement] : []),
      ...(Array.from(rootElement.querySelectorAll('img,iframe,video,source')) as HTMLElement[]),
    ].filter(isVisibleMediaElement);

    let bestCandidate: HTMLElement | null = null;
    let bestArea = Number.POSITIVE_INFINITY;

    mediaCandidates.forEach((candidate) => {
      const rect = candidate.getBoundingClientRect();
      if (!isPointInsideRect(rect, preferredHint.clientX, preferredHint.clientY)) {
        return;
      }

      const area = rect.width * rect.height;
      if (!bestCandidate || area < bestArea) {
        bestCandidate = candidate;
        bestArea = area;
      }
    });

    if (bestCandidate) {
      return bestCandidate;
    }
  }

  return null;
};

const findClosestSelectionRootElement = (element: HTMLElement | null) => {
  let current = element;
  const initialElement = element;

  while (current) {
    if (current.getAttribute('id')?.trim()) {
      return current;
    }
    current = current.parentElement;
  }

  return initialElement;
};

const resolveSelectionRootElement = (iframeDoc: Document | null | undefined, targetId?: string) => {
  if (!iframeDoc || !targetId) {
    return null;
  }

  if (isDomTargetRef(targetId)) {
    const resolvedNode = resolveDomTargetRef(iframeDoc.documentElement, targetId);
    if (isElementNode(resolvedNode)) return resolvedNode as HTMLElement;
    if (isTextNode(resolvedNode)) return resolvedNode.parentElement;
    return null;
  }

  return iframeDoc.getElementById(targetId) as HTMLElement | null;
};

const findDirectLabelSelectionTarget = (preferredHint?: PointerSelectionHint | null) => {
  const preferredTarget = preferredHint?.target || null;
  if (!preferredTarget) {
    return null;
  }

  if (preferredTarget.matches(FORM_CONTROL_SELECTOR)) {
    return null;
  }

  if (preferredTarget.matches('label')) {
    return preferredTarget;
  }

  return preferredTarget.closest('label');
};

const findPreferredDirectLabelTarget = (
  rootElement: HTMLElement,
  preferredHint?: PointerSelectionHint | null,
) => {
  const directLabelTarget = findDirectLabelSelectionTarget(preferredHint);
  if (!directLabelTarget || !rootElement.contains(directLabelTarget)) {
    return null;
  }

  return directLabelTarget;
};

const findPreferredDirectTextTarget = (
  rootElement: HTMLElement,
  preferredHint?: PointerSelectionHint | null,
) => {
  const preferredTarget = preferredHint?.target || null;
  let current: HTMLElement | null = preferredTarget;

  while (current && rootElement.contains(current)) {
    if (current.matches(FORM_CONTROL_SELECTOR) || current.matches('img,iframe,video,source')) {
      return null;
    }

    if (current.matches(TEXT_BLOCK_SELECTION_SELECTOR)) {
      return current;
    }

    if (
      current !== rootElement &&
      !hasStructuralChildElements(current) &&
      Boolean(getElementTextContent(current))
    ) {
      return current;
    }

    if (current === rootElement) {
      break;
    }

    current = current.parentElement;
  }

  return null;
};

const buildSelectionPayloadFromElement = (
  rootElement: HTMLElement,
  selectionElement: HTMLElement,
): LinkerSelectedPayload => {
  const linkElement = selectionElement.closest('a[href]');
  const href = linkElement?.getAttribute('href')?.trim();
  const documentRoot = selectionElement.ownerDocument.documentElement;
  const fallbackRootRef =
    (documentRoot && buildDomTargetRef(documentRoot, rootElement)) || undefined;

  return {
    id: rootElement.getAttribute('id')?.trim() || fallbackRootRef || '',
    text: getElementTextContent(selectionElement) || '',
    nodeName: selectionElement.tagName,
    src: isDirectMediaNodeName(selectionElement.tagName)
      ? getMediaSource(selectionElement)
      : undefined,
    href: isEditableNavigationHref(href, selectionElement.ownerDocument) ? href : undefined,
  };
};

const NON_BINDABLE_NODE_NAMES = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'HTML']);

const escapeCssSelectorToken = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/[^a-zA-Z0-9_-]/g, (char) => `\\${char}`);
};

const buildNthOfTypeSelectorSegment = (element: Element) => {
  let siblingIndex = 1;
  let previousSibling = element.previousElementSibling;

  while (previousSibling) {
    if (previousSibling.tagName === element.tagName) {
      siblingIndex += 1;
    }
    previousSibling = previousSibling.previousElementSibling;
  }

  return `${element.tagName.toLowerCase()}:nth-of-type(${siblingIndex})`;
};

const buildStableCssSelector = (element: Element) => {
  const doc = element.ownerDocument;
  const segments: string[] = [];
  let current: Element | null = element;

  while (current && current.tagName !== 'HTML') {
    const currentId = current.getAttribute('id')?.trim();
    if (currentId) {
      segments.unshift(`#${escapeCssSelectorToken(currentId)}`);
    } else {
      segments.unshift(buildNthOfTypeSelectorSegment(current));
    }

    const selector = segments.join(' > ');
    try {
      if (doc.querySelector(selector) === element) {
        return selector;
      }
    } catch {
      return undefined;
    }

    current = current.parentElement;
  }

  return segments.length > 0 ? segments.join(' > ') : undefined;
};

const resolveBindingTargetElement = (
  rootElement: HTMLElement,
  selectionElement: HTMLElement,
  preferredHint?: PointerSelectionHint | null,
) => {
  const preferredTarget = preferredHint?.target;
  if (
    preferredTarget &&
    rootElement.contains(preferredTarget) &&
    !NON_BINDABLE_NODE_NAMES.has(preferredTarget.tagName)
  ) {
    return preferredTarget;
  }

  if (!NON_BINDABLE_NODE_NAMES.has(selectionElement.tagName)) {
    return selectionElement;
  }

  return rootElement;
};

export const buildPreferredDirectLabelSelection = (preferredHint?: PointerSelectionHint | null) => {
  const directLabelTarget = findDirectLabelSelectionTarget(preferredHint);
  if (!directLabelTarget) {
    return null;
  }

  const selectionRoot = findClosestSelectionRootElement(directLabelTarget);
  if (!selectionRoot) {
    return null;
  }

  return {
    target: directLabelTarget,
    selectionRoot,
    payload: buildSelectionPayloadFromElement(selectionRoot, directLabelTarget),
  };
};

export const buildLabelSelectionFromIncomingPayload = (
  iframeDoc: Document | null | undefined,
  payload: LinkerSelectedPayload,
  preferredHint?: PointerSelectionHint | null,
) => {
  if (!iframeDoc || String(payload.nodeName || '').toUpperCase() !== 'LABEL') {
    return null;
  }

  const payloadElement = resolveSelectionRootElement(iframeDoc, payload.id);
  const hintLabelTarget = findDirectLabelSelectionTarget(preferredHint);
  const target = payloadElement?.matches('label') ? payloadElement : hintLabelTarget;

  if (!target) {
    return null;
  }

  const selectionRoot = findClosestSelectionRootElement(target);
  if (!selectionRoot) {
    return null;
  }

  return {
    target,
    selectionRoot,
    payload: buildSelectionPayloadFromElement(selectionRoot, target),
  };
};

export const buildPointerSelectionHintFromTarget = (
  target: HTMLElement,
  fallbackHint?: PointerSelectionHint | null,
): PointerSelectionHint => {
  const rect = target.getBoundingClientRect();

  return {
    target,
    clientX: fallbackHint?.clientX ?? rect.left + rect.width / 2,
    clientY: fallbackHint?.clientY ?? rect.top + rect.height / 2,
  };
};

export const getActiveDirectSelectionLock = (lock: DirectSelectionLock | null) => {
  if (!lock) {
    return null;
  }

  if (lock.expiresAt <= Date.now()) {
    return null;
  }

  return lock;
};

export const buildSelectionPromptInput = (
  selectedInfo: SelectedElementInfo,
  fallbackText?: string,
) => {
  let content = 'Edit request:';
  const textToShow =
    selectedInfo.textList && selectedInfo.textList.length > 0
      ? selectedInfo.textList.join('\n')
      : normalizeTextValue(selectedInfo.text) || normalizeTextValue(fallbackText);

  if (textToShow) {
    content = `Text: ${textToShow}\n${content}`;
  }

  if (selectedInfo.src) {
    content = `Src: ${selectedInfo.src}\n${content}`;
  }

  if (selectedInfo.href) {
    content = `Href: ${selectedInfo.href}\n${content}`;
  }

  return content;
};

export const clearManualSelectionMarker = (iframeDoc: Document) => {
  iframeDoc
    .querySelectorAll<HTMLElement>(
      `[${MANUAL_SELECTION_TARGET_ATTR}], [${MANUAL_SELECTION_SUPPRESSED_ATTR}]`,
    )
    .forEach((element) => {
      element.removeAttribute(MANUAL_SELECTION_TARGET_ATTR);
      element.removeAttribute(MANUAL_SELECTION_SUPPRESSED_ATTR);
      element.style.removeProperty('outline');
      element.style.removeProperty('outline-offset');
      element.style.removeProperty('box-shadow');
    });
};

const clearManualHoverMarker = (iframeDoc: Document) => {
  iframeDoc.querySelectorAll<HTMLElement>(`[${MANUAL_SELECTION_HOVER_ATTR}]`).forEach((element) => {
    element.removeAttribute(MANUAL_SELECTION_HOVER_ATTR);
    if (
      element.hasAttribute(MANUAL_SELECTION_TARGET_ATTR) ||
      element.hasAttribute(MANUAL_SELECTION_SUPPRESSED_ATTR)
    ) {
      return;
    }
    element.style.removeProperty('outline');
    element.style.removeProperty('outline-offset');
    element.style.removeProperty('box-shadow');
  });
};

const paintManualSelectionMarker = (
  iframeDoc: Document,
  selectionElement: HTMLElement,
  suppressedElement?: HTMLElement | null,
) => {
  const markerTarget =
    selectionElement.tagName === 'SOURCE' ? selectionElement.parentElement : selectionElement;
  if (!markerTarget) return;

  clearManualHoverMarker(iframeDoc);
  clearManualSelectionMarker(iframeDoc);

  iframeDoc.querySelectorAll(`.${LINKER_SELECTED_CLASS}`).forEach((element) => {
    if (element !== markerTarget && element !== suppressedElement) {
      element.classList.remove(LINKER_SELECTED_CLASS);
    }
  });

  if (suppressedElement && suppressedElement !== markerTarget) {
    suppressedElement.setAttribute(MANUAL_SELECTION_SUPPRESSED_ATTR, 'true');
    suppressedElement.classList.remove(LINKER_SELECTED_CLASS);
    suppressedElement.style.setProperty('outline', 'none', 'important');
    suppressedElement.style.setProperty('outline-offset', '0', 'important');
    suppressedElement.style.setProperty('box-shadow', 'none', 'important');
  }

  markerTarget.setAttribute(MANUAL_SELECTION_TARGET_ATTR, 'true');
  markerTarget.classList.add(LINKER_SELECTED_CLASS);
  markerTarget.style.setProperty('outline', '2px solid rgba(99, 102, 241, 0.95)', 'important');
  markerTarget.style.setProperty('outline-offset', '2px', 'important');
  markerTarget.style.setProperty('box-shadow', '0 0 0 1px rgba(255,255,255,0.92)', 'important');
};

export const applyManualSelectionMarker = (
  iframeDoc: Document,
  selectionElement: HTMLElement,
  suppressedElement?: HTMLElement | null,
) => {
  paintManualSelectionMarker(iframeDoc, selectionElement, suppressedElement);

  const frameWindow = iframeDoc.defaultView;
  frameWindow?.requestAnimationFrame(() => {
    paintManualSelectionMarker(iframeDoc, selectionElement, suppressedElement);
  });

  window.setTimeout(() => {
    paintManualSelectionMarker(iframeDoc, selectionElement, suppressedElement);
  }, 80);
};

const resolveElementTargetWithinRoot = (rootElement: HTMLElement, targetId?: string) => {
  if (!targetId) return null;

  if (isDomTargetRef(targetId)) {
    const resolvedNode = resolveDomTargetRef(rootElement, targetId);
    if (isElementNode(resolvedNode)) return resolvedNode as HTMLElement;
    if (isTextNode(resolvedNode)) return resolvedNode.parentElement;
    return null;
  }

  return (rootElement.ownerDocument.getElementById(targetId) as HTMLElement | null) || null;
};

export const resolveManualMediaHighlight = (
  iframeElement: HTMLIFrameElement,
  payload: LinkerSelectedPayload,
  selectedInfo: SelectedElementInfo,
  preferredHint?: PointerSelectionHint | null,
) => {
  try {
    const iframeDoc = iframeElement.contentDocument || iframeElement.contentWindow?.document;
    if (!iframeDoc || !payload.id) {
      return null;
    }

    const rootElement = resolveSelectionRootElement(iframeDoc, payload.id);
    if (!rootElement) {
      return null;
    }

    let highlightTarget = resolveElementTargetWithinRoot(rootElement, selectedInfo.srcTargetId);

    if (!highlightTarget?.matches('img,iframe,video,source')) {
      highlightTarget = findMatchingMediaElement(rootElement, selectedInfo.src || payload.src);
    }

    if (!highlightTarget && isDirectMediaNodeName(selectedInfo.nodeName)) {
      highlightTarget = findPreferredDirectMediaTarget(rootElement, payload, preferredHint);
    }

    if (!highlightTarget?.matches('img,iframe,video,source')) {
      return null;
    }

    return {
      target: highlightTarget,
      suppressedElement: rootElement,
    };
  } catch (error) {
    console.warn('[Linker] Failed to resolve manual media highlight target:', error);
    return null;
  }
};

const buildNodeTargetRef = (rootElement: HTMLElement, targetNode: Node) =>
  isElementNode(targetNode)
    ? targetNode.getAttribute('id')?.trim() || buildDomTargetRef(rootElement, targetNode)
    : buildDomTargetRef(rootElement, targetNode);

const resolveTextTargetNode = (rootElement: HTMLElement, textNode: Text) => {
  const parentElement = textNode.parentElement;
  if (!parentElement) return rootElement as Node;

  if (parentElement === rootElement) {
    return hasStructuralChildElements(rootElement) ? textNode : rootElement;
  }

  const parentId = parentElement.getAttribute('id')?.trim();
  if (parentId || parentElement.matches('a,button,label')) {
    return parentElement;
  }

  if (
    !hasStructuralChildElements(parentElement) ||
    INLINE_TEXT_WRAPPER_TAGS.has(parentElement.tagName)
  ) {
    return parentElement;
  }

  return textNode;
};

const collectTextTargets = (
  targetRoot: HTMLElement,
  element: HTMLElement,
): SelectedElementValueTarget[] | undefined => {
  if (element.matches(FORM_CONTROL_SELECTOR)) {
    const value = getDirectFormControlValue(element);
    if (!value) {
      return undefined;
    }

    return [
      {
        id: buildNodeTargetRef(targetRoot, element) || undefined,
        nodeName: element.tagName,
        value,
      } satisfies SelectedElementValueTarget,
    ];
  }

  const targetOrder: string[] = [];
  const targets = new Map<
    string,
    {
      node: Node;
      nodeName: string;
      values: string[];
    }
  >();
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const value = normalizeTextValue(textNode.textContent);
    if (!value) continue;

    const targetNode = resolveTextTargetNode(element, textNode);
    const targetId = buildNodeTargetRef(targetRoot, targetNode);
    const targetKey =
      targetId ||
      `root:${element.tagName}:${targetNode.nodeType === Node.TEXT_NODE ? 'text' : 'element'}`;

    if (!targets.has(targetKey)) {
      targets.set(targetKey, {
        node: targetNode,
        nodeName: isElementNode(targetNode)
          ? targetNode.tagName
          : textNode.parentElement?.tagName || '#TEXT',
        values: [],
      });
      targetOrder.push(targetKey);
    }

    const target = targets.get(targetKey);
    if (!target) continue;

    const lastValue = target.values[target.values.length - 1];
    if (lastValue !== value) {
      target.values.push(value);
    }
  }

  const resolvedTargets = targetOrder
    .flatMap((targetKey) => {
      const target = targets.get(targetKey);
      if (!target) return [];

      const joinedValue = normalizeTextValue(target.values.join(' '));
      if (!joinedValue) return [];

      return [
        {
          id: buildNodeTargetRef(targetRoot, target.node) || undefined,
          nodeName: target.nodeName,
          value: joinedValue,
        } satisfies SelectedElementValueTarget,
      ];
    })
    .slice(0, 8);

  return resolvedTargets.length > 0 ? resolvedTargets : undefined;
};

const collectLinkElements = (element: HTMLElement) => {
  const links: HTMLAnchorElement[] = [];
  const seen = new Set<HTMLAnchorElement>();

  const appendLink = (link: HTMLAnchorElement) => {
    if (seen.has(link)) return;

    const href = link.getAttribute('href')?.trim();
    if (!isEditableNavigationHref(href, element.ownerDocument)) return;

    seen.add(link);
    links.push(link);
  };

  if (element.matches('a[href]')) {
    appendLink(element as HTMLAnchorElement);
  }

  element.querySelectorAll('a[href]').forEach((link) => {
    appendLink(link as HTMLAnchorElement);
  });

  return links;
};

const pushSelectionNode = (nodes: SelectedElementNode[], node: SelectedElementNode) => {
  const exists = nodes.some(
    (item) =>
      item.kind === node.kind &&
      item.nodeName === node.nodeName &&
      (item.id || '') === (node.id || '') &&
      (item.className || '') === (node.className || '') &&
      (item.href || '') === (node.href || '') &&
      (item.src || '') === (node.src || ''),
  );
  if (!exists) {
    nodes.push(node);
  }
};

export const buildSelectedElementInfo = (
  iframeElement: HTMLIFrameElement,
  data: LinkerSelectedPayload,
  preferredHint?: PointerSelectionHint | null,
): SelectedElementInfo => {
  let selectedElementId = data.id;
  let selectedNodeName = data.nodeName;
  let selectedText = normalizeTextValue(data.text) || String(data.text || '');
  let selfClassName: string | undefined;
  const selectionNodes: SelectedElementNode[] = [];
  let textList: string[] | undefined;
  let textTargetId = selectedElementId;
  let textTargets: SelectedElementValueTarget[] | undefined;
  let bindingTargetId: string | undefined;
  let bindingTargetSelector: string | undefined;
  let bindingTargetNodeName: string | undefined;
  let bindingTargetText: string | undefined;
  let resolvedSrc = isDirectMediaNodeName(data.nodeName) ? data.src?.trim() : undefined;
  let resolvedHref: string | undefined;
  let srcTargetId = resolvedSrc ? data.id : undefined;
  let hrefTargetId: string | undefined;
  let hrefTargets: SelectedElementValueTarget[] | undefined;

  try {
    const iframeDoc = iframeElement.contentDocument || iframeElement.contentWindow?.document;
    if (iframeDoc && data.id) {
      const rootElement = resolveSelectionRootElement(iframeDoc, data.id);
      if (rootElement) {
        const markedElement =
          (iframeDoc.querySelector(`.${LINKER_SELECTED_CLASS}`) as HTMLElement | null) || null;
        const preferredLabelTarget = findPreferredDirectLabelTarget(rootElement, preferredHint);
        const preferredMediaTarget = findPreferredDirectMediaTarget(
          rootElement,
          data,
          preferredHint,
        );
        const preferredTextTarget = findPreferredDirectTextTarget(rootElement, preferredHint);
        const inferredSelectionScope =
          preferredLabelTarget ||
          preferredMediaTarget ||
          findBestSelectionScope(rootElement, data, markedElement);
        const selectionScopeElement = inferredSelectionScope || preferredTextTarget || rootElement;
        const bindingTargetElement = resolveBindingTargetElement(
          rootElement,
          selectionScopeElement,
          preferredHint,
        );
        const getTargetRefWithinRoot = (targetNode: Node) =>
          buildNodeTargetRef(rootElement, targetNode) || undefined;
        const scopeId = selectionScopeElement.getAttribute('id')?.trim();
        const scopeTargetId = getTargetRefWithinRoot(selectionScopeElement);
        selectedElementId = rootElement.getAttribute('id')?.trim() || data.id;
        selectedNodeName = selectionScopeElement.tagName;
        selectedText = getElementTextContent(selectionScopeElement) || selectedText;
        selfClassName = getElementClassName(selectionScopeElement);
        bindingTargetId = bindingTargetElement.getAttribute('id')?.trim() || undefined;
        bindingTargetSelector = bindingTargetId
          ? undefined
          : buildStableCssSelector(bindingTargetElement);
        bindingTargetNodeName = bindingTargetElement.tagName;
        bindingTargetText = getElementTextContent(bindingTargetElement) || undefined;

        resolvedHref = isEditableNavigationHref(data.href, iframeDoc)
          ? data.href?.trim()
          : undefined;
        hrefTargetId = resolvedHref
          ? getTargetRefWithinRoot(selectionScopeElement) || selectedElementId
          : undefined;

        const canUseDirectMedia = isDirectMediaNodeName(selectionScopeElement.tagName);
        pushSelectionNode(selectionNodes, {
          id: scopeId || scopeTargetId || undefined,
          nodeName: selectionScopeElement.tagName,
          kind: 'self',
          className: selfClassName,
          src:
            canUseDirectMedia && selectionScopeElement.matches('img,iframe,video,source')
              ? getMediaSource(selectionScopeElement) || resolvedSrc
              : undefined,
          href:
            selectionScopeElement.matches('a[href]') &&
            isEditableNavigationHref(selectionScopeElement.getAttribute('href'), iframeDoc)
              ? selectionScopeElement.getAttribute('href')?.trim() || resolvedHref
              : undefined,
        });

        textTargets = collectTextTargets(rootElement, selectionScopeElement);
        textList = textTargets?.map((target) => target.value);
        textTargetId = textTargets?.[0]?.id || selectedElementId;

        const mediaNode = selectionScopeElement.matches('img,iframe,video,source')
          ? selectionScopeElement
          : null;
        const mediaSrc = getMediaSource(mediaNode);
        if (mediaNode && mediaSrc) {
          resolvedSrc = resolvedSrc || mediaSrc;
          srcTargetId = getTargetRefWithinRoot(mediaNode) || srcTargetId || selectedElementId;
        } else {
          const descendantMedia = findRelevantDescendantMedia(selectionScopeElement);
          if (descendantMedia) {
            resolvedSrc = resolvedSrc || descendantMedia.src;
            srcTargetId =
              getTargetRefWithinRoot(descendantMedia.element) || srcTargetId || selectedElementId;
            pushSelectionNode(selectionNodes, {
              id: descendantMedia.element.getAttribute('id')?.trim() || undefined,
              nodeName: descendantMedia.element.tagName,
              kind: 'image',
              className: getElementClassName(descendantMedia.element),
              src: descendantMedia.src,
            });
          }
        }

        const linkNodes = collectLinkElements(selectionScopeElement);
        if (linkNodes.length > 0) {
          hrefTargets = linkNodes.map((linkNode) => ({
            id: getTargetRefWithinRoot(linkNode) || undefined,
            nodeName: linkNode.tagName,
            value: linkNode.getAttribute('href')?.trim() || '',
          }));

          const primaryHrefTarget = hrefTargets[0];
          if (primaryHrefTarget) {
            resolvedHref = resolvedHref || primaryHrefTarget.value;
            hrefTargetId = primaryHrefTarget.id || hrefTargetId || selectedElementId;
          }

          linkNodes.forEach((linkNode) => {
            if (linkNode === selectionScopeElement) return;
            pushSelectionNode(selectionNodes, {
              id: linkNode.getAttribute('id')?.trim() || undefined,
              nodeName: linkNode.tagName,
              kind: 'link',
              className: getElementClassName(linkNode),
              href: linkNode.getAttribute('href')?.trim() || undefined,
            });
          });
        }
      }
    }
  } catch (err) {
    console.warn('[Linker] Failed to inspect selected element subtree:', err);
  }

  if (!resolvedHref && isEditableNavigationHref(data.href)) {
    resolvedHref = data.href?.trim();
    hrefTargetId = resolvedHref ? selectedElementId : undefined;
  }

  if (selectionNodes.length === 0) {
    pushSelectionNode(selectionNodes, {
      id: data.id,
      nodeName: data.nodeName,
      kind: 'self',
      src: isDirectMediaNodeName(data.nodeName) ? data.src : undefined,
      href: resolvedHref,
    });
  }

  return {
    ...data,
    id: selectedElementId,
    text: selectedText || '',
    nodeName: selectedNodeName,
    bindingTargetId,
    bindingTargetSelector,
    bindingTargetNodeName,
    bindingTargetText,
    textList,
    textTargetId,
    textTargets,
    src: resolvedSrc,
    href: resolvedHref,
    srcTargetId,
    hrefTargetId,
    hrefTargets,
    selectionNodes,
  };
};

type StudioPreviewProps = {
  previewUrl: string;
  previewUrlLoading?: boolean;
};

export const StudioPreview = ({ previewUrl, previewUrlLoading = false }: StudioPreviewProps) => {
  const { t } = useTranslation('studio');
  const {
    previewDevice,
    isBuilding,
    isBuildDone,
    currentId,
    currentProjectId,
    iframeUrl,
    currentProjectTypeId,
    iframeElement,
    setIframeElement,
    generatedHtml,
    previewHtmlOverride,
    previewReview,
    isPreviewApplying,
    clearPreviewReview,
    startPreviewReview,
    setPreviewReviewView,
    setCurrentDraftMeta,
    lastPublishedUrl,
    setGeneratedHtml,
    setPreviewHtmlOverride,
    currentDraftId,
    currentDraftVersion,
    currentDraftStatus,
  } = useStudioStore();

  const updateChatMessage = useExpertStore((state) => state.updateChatMessage);

  const { selectedElement, setPromptInput, setSelectedElement, setLinkerHost, isEditing } =
    useCreatorStore();

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [iframeLoadVersion, setIframeLoadVersion] = useState(0);
  const [isReviewDecisionPending, setIsReviewDecisionPending] = useState(false);
  const [previewRecoveryStatus, setPreviewRecoveryStatus] = useState<PreviewRecoveryStatus>('idle');
  const [previewRecoveryAttempt, setPreviewRecoveryAttempt] = useState(0);
  const [remotePreviewHtmlOverride, setRemotePreviewHtmlOverride] = useState<string | null>(null);
  const lastPointerSelectionHintRef = useRef<PointerSelectionHint | null>(null);
  const directSelectionLockRef = useRef<DirectSelectionLock | null>(null);
  const previewScrollPositionsRef = useRef<
    Record<PreviewScrollBucket, PreviewScrollPosition | null>
  >({
    default: null,
    before: null,
    after: null,
  });
  const restorePreviewScrollBucketRef = useRef<PreviewScrollBucket | null>(null);
  const restoreScrollTimersRef = useRef<number[]>([]);
  const previewReviewSessionKeyRef = useRef<string | null>(null);
  const isAiWebpage = !isSpecialProjectType(currentProjectTypeId);
  const activeChatProjectId = (isAiWebpage ? currentId : currentProjectId) ?? currentProjectId;
  const currentPreviewScrollBucket = useMemo(
    () => getPreviewScrollBucket(previewReview.active, previewReview.view),
    [previewReview.active, previewReview.view],
  );

  const displayedGeneratedHtml = useMemo(() => {
    if (!previewReview.active) {
      return previewHtmlOverride || generatedHtml || remotePreviewHtmlOverride;
    }
    return previewReview.view === 'before' ? previewReview.beforeHtml : previewReview.afterHtml;
  }, [generatedHtml, previewHtmlOverride, previewReview, remotePreviewHtmlOverride]);

  const processedHtml = useMemo(
    () =>
      displayedGeneratedHtml
        ? injectLinkInterceptor(
            stripLegacyLandingPageTrackingScript(
              stripLegacyGoogleAdsTrackingScript(
                stripStudioAnalyticsTrackingScript(
                  stripStudioGoogleAdsTrackingScript(displayedGeneratedHtml),
                ),
              ),
            ),
          )
        : null,
    [displayedGeneratedHtml],
  );
  const previewContentKey = useMemo(
    () => (processedHtml ? getHtmlContentKey(processedHtml) : iframeUrl || 'empty'),
    [iframeUrl, processedHtml],
  );
  const hasPreviewSource = Boolean(processedHtml || iframeUrl);
  const shouldRecoverEmptyAiPreview = Boolean(
    isAiWebpage &&
    !isBuilding &&
    !iframeUrl &&
    !generatedHtml.trim() &&
    !previewReview.active &&
    currentId,
  );
  const shouldShowPreviewRecovery = Boolean(
    isAiWebpage && !isBuilding && !iframeUrl && !generatedHtml.trim() && !previewReview.active,
  );
  const isPreviewFrameLoading = hasPreviewSource && !isIframeLoaded;
  const previewReviewSessionKey = useMemo(() => {
    if (!previewReview.active) return null;
    return [
      previewReview.draftId ?? 'draftless',
      previewReview.version ?? 'noversion',
      getHtmlContentKey(previewReview.beforeHtml || ''),
      getHtmlContentKey(previewReview.afterHtml || ''),
    ].join(':');
  }, [
    previewReview.active,
    previewReview.afterHtml,
    previewReview.beforeHtml,
    previewReview.draftId,
    previewReview.version,
  ]);

  const clearScheduledScrollRestores = useCallback(() => {
    restoreScrollTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    restoreScrollTimersRef.current = [];
  }, []);

  const rememberPreviewScroll = useCallback(
    (bucket: PreviewScrollBucket = currentPreviewScrollBucket) => {
      const position = getPreviewScrollPosition(iframeElement);
      if (!position) return false;

      previewScrollPositionsRef.current[bucket] = position;
      return true;
    },
    [currentPreviewScrollBucket, iframeElement],
  );

  const syncDefaultPreviewScrollPosition = useCallback(
    (sourceBucket: PreviewScrollBucket) => {
      if (currentPreviewScrollBucket === sourceBucket) {
        rememberPreviewScroll(sourceBucket);
      }

      const sourcePosition =
        previewScrollPositionsRef.current[sourceBucket] ??
        previewScrollPositionsRef.current[currentPreviewScrollBucket];
      if (!sourcePosition) {
        return false;
      }

      previewScrollPositionsRef.current.default = clonePreviewScrollPosition(sourcePosition);
      restorePreviewScrollBucketRef.current = 'default';
      return true;
    },
    [currentPreviewScrollBucket, rememberPreviewScroll],
  );

  const preparePreviewScrollRestore = useCallback(
    (targetBucket: PreviewScrollBucket) => {
      const currentBucket = currentPreviewScrollBucket;
      rememberPreviewScroll(currentBucket);

      if (
        !previewScrollPositionsRef.current[targetBucket] &&
        previewScrollPositionsRef.current[currentBucket]
      ) {
        previewScrollPositionsRef.current[targetBucket] = clonePreviewScrollPosition(
          previewScrollPositionsRef.current[currentBucket] as PreviewScrollPosition,
        );
      }

      restorePreviewScrollBucketRef.current = targetBucket;
    },
    [currentPreviewScrollBucket, rememberPreviewScroll],
  );

  const schedulePreviewScrollRestore = useCallback(() => {
    const restoreBucket = restorePreviewScrollBucketRef.current;
    const restorePosition = restoreBucket ? previewScrollPositionsRef.current[restoreBucket] : null;
    if (!restoreBucket || !restorePosition) {
      restorePreviewScrollBucketRef.current = null;
      return;
    }

    clearScheduledScrollRestores();

    [0, 80, 220].forEach((delayMs, index, delays) => {
      const timerId = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          restorePreviewScrollPosition(iframeElement, restorePosition);
          if (index === delays.length - 1) {
            restorePreviewScrollBucketRef.current = null;
          }
        });
      }, delayMs);

      restoreScrollTimersRef.current.push(timerId);
    });
  }, [clearScheduledScrollRestores, iframeElement]);

  useEffect(() => {
    if (!previewReview.active) {
      previewReviewSessionKeyRef.current = null;
      previewScrollPositionsRef.current.before = null;
      previewScrollPositionsRef.current.after = null;
      return;
    }

    if (previewReviewSessionKeyRef.current === previewReviewSessionKey) {
      return;
    }

    previewReviewSessionKeyRef.current = previewReviewSessionKey;
    previewScrollPositionsRef.current.before = null;
    previewScrollPositionsRef.current.after = null;

    const defaultPosition = previewScrollPositionsRef.current.default;
    if (defaultPosition) {
      previewScrollPositionsRef.current.before = clonePreviewScrollPosition(defaultPosition);
      previewScrollPositionsRef.current.after = clonePreviewScrollPosition(defaultPosition);
    }

    restorePreviewScrollBucketRef.current = previewReview.view;
  }, [previewReview.active, previewReview.view, previewReviewSessionKey]);

  useLayoutEffect(() => {
    return () => {
      if (rememberPreviewScroll(currentPreviewScrollBucket)) {
        restorePreviewScrollBucketRef.current =
          restorePreviewScrollBucketRef.current ?? currentPreviewScrollBucket;
      }
    };
    // previewContentKey changes whenever review before/after html swaps or the rendered iframe source changes.
  }, [currentPreviewScrollBucket, previewContentKey, rememberPreviewScroll]);

  useEffect(() => {
    if (!iframeElement || !isIframeLoaded) {
      return;
    }

    const handleScroll = () => {
      const position = getPreviewScrollPosition(iframeElement);
      if (position) {
        previewScrollPositionsRef.current[currentPreviewScrollBucket] = position;
      }
    };

    try {
      const iframeWindow = iframeElement.contentWindow;
      if (!iframeWindow) {
        return;
      }

      handleScroll();
      iframeWindow.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        iframeWindow.removeEventListener('scroll', handleScroll);
      };
    } catch {
      return;
    }
  }, [currentPreviewScrollBucket, iframeElement, isIframeLoaded]);

  useEffect(() => {
    return () => {
      clearScheduledScrollRestores();
    };
  }, [clearScheduledScrollRestores]);

  useEffect(() => {
    if (!hasPreviewSource) {
      setIsIframeLoaded(false);
      setIframeLoadVersion(0);
      return;
    }

    setIsIframeLoaded(false);
    setIframeLoadVersion(0);
    const fallbackTimer = window.setTimeout(() => {
      setIsIframeLoaded(true);
    }, 1200);

    return () => window.clearTimeout(fallbackTimer);
  }, [hasPreviewSource, previewContentKey]);

  useEffect(() => {
    if (!shouldRecoverEmptyAiPreview) {
      setPreviewRecoveryStatus('idle');
      setPreviewRecoveryAttempt(0);
      return;
    }

    let cancelled = false;

    const recoverPreviewDraft = async () => {
      setPreviewRecoveryStatus('recovering');
      setPreviewRecoveryAttempt(0);

      for (let attempt = 1; attempt <= PREVIEW_DRAFT_RECOVERY_MAX_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        setPreviewRecoveryAttempt(attempt);

        try {
          const { editableDraft, pendingReview } = await resolveEditableDraft(Number(currentId));
          if (cancelled) return;

          if (pendingReview) {
            clearPreviewReview();
            setGeneratedHtml(pendingReview.beforeHtml);
            setCurrentDraftMeta(
              pendingReview.currentDraft.id,
              pendingReview.currentDraft.version,
              pendingReview.currentDraft.draftStatus,
            );
            startPreviewReview({
              draftId: pendingReview.pendingDraft.id,
              version: pendingReview.pendingDraft.version,
              beforeHtml: pendingReview.beforeHtml,
              afterHtml: pendingReview.afterHtml,
              title: 'Review latest change',
              note: 'Switch between the current draft and the proposed edit, then decide which version to keep.',
              sourceMessageId: null,
            });
            setPreviewRecoveryStatus('idle');
            return;
          }

          if (editableDraft?.content) {
            clearPreviewReview();
            setGeneratedHtml(buildDraftPreviewHtml(editableDraft.content));
            setCurrentDraftMeta(editableDraft.id, editableDraft.version, editableDraft.draftStatus);
            setPreviewRecoveryStatus('idle');
            return;
          }
        } catch (error) {
          console.warn('[StudioPreview] Failed to recover empty preview draft:', error);
        }

        if (attempt < PREVIEW_DRAFT_RECOVERY_MAX_ATTEMPTS) {
          await waitForDraftSync(PREVIEW_DRAFT_RECOVERY_INTERVAL_MS);
        }
      }

      if (!cancelled) {
        setPreviewRecoveryStatus('timed_out');
      }
    };

    void recoverPreviewDraft();

    return () => {
      cancelled = true;
    };
  }, [
    clearPreviewReview,
    currentId,
    setCurrentDraftMeta,
    setGeneratedHtml,
    shouldRecoverEmptyAiPreview,
    startPreviewReview,
  ]);

  useEffect(() => {
    if (
      !isAiWebpage ||
      isBuilding ||
      previewReview.active ||
      currentDraftStatus !== 'CONFIRMED' ||
      !generatedHtml.trim() ||
      previewUrlLoading ||
      !previewUrl
    ) {
      setPreviewHtmlOverride(null);
      return;
    }

    let cancelled = false;
    const expectedDraftId = String(currentDraftId ?? '');
    const expectedDraftVersion = currentDraftVersion ?? null;

    const syncPermanentPreview = async () => {
      const preview = await loadPermanentPreviewHtml(previewUrl);
      if (cancelled || !preview?.html) return;

      const nextPreviewHtml = buildPermanentPreviewHtml(preview.html, preview.previewUrl);
      const latestState = useStudioStore.getState();
      const currentStateDraftId = String(latestState.currentDraftId ?? '');
      const currentStateDraftVersion = latestState.currentDraftVersion ?? null;

      if (
        latestState.isBuilding ||
        latestState.previewReview.active ||
        latestState.currentDraftStatus !== 'CONFIRMED' ||
        currentStateDraftId !== expectedDraftId ||
        currentStateDraftVersion !== expectedDraftVersion
      ) {
        return;
      }

      setPreviewHtmlOverride(nextPreviewHtml);
    };

    void syncPermanentPreview();

    return () => {
      cancelled = true;
    };
  }, [
    currentDraftId,
    currentDraftStatus,
    currentDraftVersion,
    generatedHtml,
    isAiWebpage,
    isBuilding,
    previewUrl,
    previewUrlLoading,
    previewReview.active,
    setPreviewHtmlOverride,
  ]);

  useEffect(() => {
    if (isAiWebpage || isBuilding || previewReview.active || !iframeUrl || generatedHtml.trim()) {
      setRemotePreviewHtmlOverride(null);
      return;
    }

    let cancelled = false;

    const syncRemotePreview = async () => {
      const preview = await loadPermanentPreviewHtml(iframeUrl, { allowCrossOrigin: true });
      if (cancelled) return;

      if (!preview?.html) {
        setRemotePreviewHtmlOverride(null);
        return;
      }

      setRemotePreviewHtmlOverride(buildPermanentPreviewHtml(preview.html, preview.previewUrl));
    };

    void syncRemotePreview();

    return () => {
      cancelled = true;
    };
  }, [generatedHtml, iframeUrl, isAiWebpage, isBuilding, previewReview.active]);

  const applySelectedInfo = useCallback(
    (selectedInfo: SelectedElementInfo, fallbackText?: string) => {
      setSelectedElement(selectedInfo);
      setPromptInput(buildSelectionPromptInput(selectedInfo, fallbackText));
    },
    [setPromptInput, setSelectedElement],
  );

  useEffect(() => {
    const iframeWindow = iframeElement?.contentWindow;
    const iframeDoc = iframeElement?.contentDocument || iframeWindow?.document;
    if (!iframeWindow || !iframeDoc || previewReview.active || iframeLoadVersion <= 0) {
      lastPointerSelectionHintRef.current = null;
      directSelectionLockRef.current = null;
      return;
    }

    const rememberPointerTarget = (event: Event) => {
      const supportsPointerEvents = 'onpointerdown' in iframeWindow;
      const shouldResetDirectSelectionLock =
        event.type === 'pointerdown' || (event.type === 'mousedown' && !supportsPointerEvents);

      if (shouldResetDirectSelectionLock) {
        directSelectionLockRef.current = null;
      }

      const rawTarget = event.target;
      const target = asSelectionElement(rawTarget);

      const pointerEvent = event as MouseEvent;
      lastPointerSelectionHintRef.current = {
        target,
        clientX: pointerEvent.clientX,
        clientY: pointerEvent.clientY,
      };
    };

    try {
      iframeWindow.addEventListener('pointerdown', rememberPointerTarget, true);
      iframeWindow.addEventListener('mousedown', rememberPointerTarget, true);
    } catch (error) {
      console.warn('[Linker] Failed to attach pointer target listener:', error);
      return;
    }

    return () => {
      iframeWindow.removeEventListener('pointerdown', rememberPointerTarget, true);
      iframeWindow.removeEventListener('mousedown', rememberPointerTarget, true);
      lastPointerSelectionHintRef.current = null;
    };
  }, [displayedGeneratedHtml, iframeElement, iframeLoadVersion, iframeUrl, previewReview.active]);

  useEffect(() => {
    if (selectedElement) {
      return;
    }

    const iframeDoc = iframeElement?.contentDocument || iframeElement?.contentWindow?.document;
    if (iframeDoc) {
      clearManualSelectionMarker(iframeDoc);
    }
  }, [iframeElement, selectedElement]);

  useEffect(() => {
    const currentUrl = displayedGeneratedHtml ? 'srcdoc' : iframeUrl || null;
    if (!currentUrl || !iframeElement || iframeLoadVersion <= 0) {
      return;
    }

    const host = createLinkerHost(iframeElement);

    if (previewReview.active) {
      setSelectedElement(null);
      setLinkerHost(null);

      host.on(MessageType.PING, () => {
        host.send(MessageType.DEACTIVATE, {});
      });

      host.send(MessageType.DEACTIVATE, {});

      return () => {
        host.send?.(MessageType.DEACTIVATE, {});
        host.destroy?.();
        setLinkerHost(null);
      };
    }

    setLinkerHost(host);

    host.on(MessageType.ELEMENT_SELECTED, (data: LinkerSelectedPayload) => {
      const iframeDoc = iframeElement.contentDocument || iframeElement.contentWindow?.document;
      const preferredHint = lastPointerSelectionHintRef.current;
      const incomingLabelSelection = buildLabelSelectionFromIncomingPayload(
        iframeDoc,
        data,
        preferredHint,
      );
      if (incomingLabelSelection) {
        directSelectionLockRef.current = {
          ...incomingLabelSelection,
          expiresAt: Date.now() + DIRECT_SELECTION_LOCK_MS,
        };
      }
      const activeDirectSelectionLock = getActiveDirectSelectionLock(
        directSelectionLockRef.current,
      );
      if (!activeDirectSelectionLock && directSelectionLockRef.current) {
        directSelectionLockRef.current = null;
      }
      const lockedLabelSelection = activeDirectSelectionLock?.target.matches('label')
        ? {
            target: activeDirectSelectionLock.target,
            selectionRoot: activeDirectSelectionLock.selectionRoot,
            payload: activeDirectSelectionLock.payload,
          }
        : null;
      const preferredLabelSelection =
        lockedLabelSelection ||
        incomingLabelSelection ||
        buildPreferredDirectLabelSelection(preferredHint);
      const shouldForceLockedLabelSelection = Boolean(lockedLabelSelection);
      const shouldPreferLabelSelection =
        Boolean(preferredLabelSelection) &&
        FORM_CONTROL_NODE_NAMES.has(String(data.nodeName || '').toUpperCase());
      const effectiveData =
        shouldForceLockedLabelSelection && lockedLabelSelection
          ? lockedLabelSelection.payload
          : shouldPreferLabelSelection && preferredLabelSelection
            ? preferredLabelSelection.payload
            : data;
      const effectiveHint: PointerSelectionHint | null =
        shouldForceLockedLabelSelection && lockedLabelSelection
          ? buildPointerSelectionHintFromTarget(lockedLabelSelection.target, preferredHint)
          : shouldPreferLabelSelection && preferredLabelSelection
            ? buildPointerSelectionHintFromTarget(preferredLabelSelection.target, preferredHint)
            : preferredHint;

      if (iframeDoc) {
        clearManualSelectionMarker(iframeDoc);
      }

      const selectedInfo = buildSelectedElementInfo(iframeElement, effectiveData, effectiveHint);
      lastPointerSelectionHintRef.current = null;
      applySelectedInfo(selectedInfo, effectiveData.text);

      if (iframeDoc) {
        if (
          (shouldForceLockedLabelSelection && lockedLabelSelection) ||
          (shouldPreferLabelSelection && preferredLabelSelection)
        ) {
          const labelSelection = lockedLabelSelection || preferredLabelSelection;
          if (!labelSelection) {
            return;
          }
          applyManualSelectionMarker(
            iframeDoc,
            labelSelection.target,
            labelSelection.selectionRoot === labelSelection.target
              ? null
              : labelSelection.selectionRoot,
          );
        } else {
          const manualMediaHighlight = resolveManualMediaHighlight(
            iframeElement,
            effectiveData,
            selectedInfo,
            effectiveHint,
          );

          if (manualMediaHighlight) {
            applyManualSelectionMarker(
              iframeDoc,
              manualMediaHighlight.target,
              manualMediaHighlight.suppressedElement,
            );
          }
        }
      }
    });

    host.on(MessageType.PING, () => {
      host.send(MessageType.ACTIVATE, {});
    });

    host.send(MessageType.ACTIVATE, {});

    return () => {
      host.send?.(MessageType.DEACTIVATE, {});
      host.destroy?.();
      setLinkerHost(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applySelectedInfo,
    displayedGeneratedHtml,
    iframeElement,
    iframeLoadVersion,
    iframeUrl,
    previewReview.active,
  ]);

  if (isBuilding === null && iframeUrl === null && !generatedHtml) {
    return <StudioPreviewSkeleton />;
  }

  if (isBuilding && !iframeUrl && (!generatedHtml || isBuildDone)) {
    return <StudioBuildStage />;
  }

  if (
    !shouldShowPreviewRecovery &&
    !isBuilding &&
    !iframeUrl &&
    !generatedHtml &&
    !previewReview.active
  ) {
    return <StudioPreviewSkeleton />;
  }

  const renderPreviewContent = () => {
    let view = null;
    const handlePreviewLoad = () => {
      const iframeDoc = iframeElement?.contentDocument || iframeElement?.contentWindow?.document;
      try {
        removeLegacyLandingPageTrackingScriptsFromDocument(iframeDoc);
        removeLegacyGoogleAdsTrackingScriptsFromDocument(iframeDoc);
        removeStudioGoogleAdsTrackingScriptsFromDocument(iframeDoc);
        removeStudioAnalyticsTrackingScriptsFromDocument(iframeDoc);
      } catch {
        // Ignore cross-origin iframe access failures.
      }
      setIsIframeLoaded(true);
      setIframeLoadVersion((version) => version + 1);
      schedulePreviewScrollRestore();
    };

    if (shouldShowPreviewRecovery) {
      const isTimedOut = previewRecoveryStatus === 'timed_out';
      const progressPercent = Math.min(
        100,
        Math.round((previewRecoveryAttempt / PREVIEW_DRAFT_RECOVERY_MAX_ATTEMPTS) * 100),
      );

      return (
        <div className="flex h-full min-h-[800px] w-full items-center justify-center bg-white p-8">
          <div className="grid w-full max-w-[360px] justify-items-center gap-4 text-center">
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full border',
                isTimedOut
                  ? 'border-[rgba(220,38,38,0.18)] bg-[rgba(220,38,38,0.08)] text-[#B91C1C]'
                  : 'border-[rgba(109,40,217,0.16)] bg-[rgba(109,40,217,0.08)] text-[#6D28D9]',
              )}
            >
              <Icon
                icon={isTimedOut ? 'fa6-solid:triangle-exclamation' : 'fa6-solid:spinner'}
                className={cn('h-4 w-4', !isTimedOut && 'animate-spin')}
              />
            </div>
            <div className="grid gap-1">
              <div className="text-[15px] font-bold text-[#0D0D1A]">
                {isTimedOut ? 'Preview is still preparing' : 'Preparing preview'}
              </div>
              <div className="text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
                {isTimedOut
                  ? 'The latest draft has not returned yet. Please wait a moment and reopen this page.'
                  : 'Loading the latest generated draft...'}
              </div>
            </div>
            {!isTimedOut ? (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[rgba(15,23,42,0.08)]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#6D28D9] to-[#0891B2] transition-all duration-300"
                  style={{ width: `${Math.max(8, progressPercent)}%` }}
                />
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    if (processedHtml) {
      const contentKey = getHtmlContentKey(processedHtml);
      view = (
        <iframe
          key={contentKey}
          ref={setIframeElement}
          name={STUDIO_PREVIEW_WINDOW_NAME}
          srcDoc={processedHtml}
          onLoad={handlePreviewLoad}
          sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
          className={cn(
            'preview-iframe h-full w-full border-none transition-opacity duration-300',
            !isIframeLoaded && 'opacity-0',
          )}
          title={t('preview.title')}
        />
      );
    } else if (isSpecialProjectType(currentProjectTypeId)) {
      if (iframeUrl) {
        view = (
          <iframe
            key="iframe-url"
            ref={setIframeElement}
            name={STUDIO_PREVIEW_WINDOW_NAME}
            src={iframeUrl}
            onLoad={handlePreviewLoad}
            sandbox="allow-scripts allow-forms allow-modals"
            className={cn(
              'preview-iframe h-full w-full border-none transition-opacity duration-300',
              !isIframeLoaded && 'opacity-0',
            )}
            title={t('preview.title')}
          />
        );
      }
    }

    return view;
  };

  const displayUrl = previewUrlLoading ? t('preview.loading') : previewUrl;
  const canOpenPreview = Boolean(previewUrl) && !previewUrlLoading;
  const publishedViewUrl = lastPublishedUrl
    ? normalizeLegacyPlatformUrlWithTrailingSlash(lastPublishedUrl)
    : '';
  const canOpenPublishedView = Boolean(publishedViewUrl);

  const handleAcceptPreviewVersion = async () => {
    if (!previewReview.active) return;

    setIsReviewDecisionPending(true);
    try {
      syncDefaultPreviewScrollPosition('after');

      if (previewReview.draftId) {
        await projectApi.updateDraftStatus({
          draftId: previewReview.draftId,
          draftStatus: 'CONFIRMED',
        });
        rememberRecentDraftStatus(previewReview.draftId, 'CONFIRMED');
      }

      setGeneratedHtml(previewReview.afterHtml);
      if (previewReview.draftId) {
        setCurrentDraftMeta(previewReview.draftId, previewReview.version, 'CONFIRMED');
      } else {
        setCurrentDraftMeta(null, null, 'PENDING');
      }

      if (previewReview.sourceMessageId) {
        updateChatMessage(
          previewReview.sourceMessageId,
          {
            status: 'applied',
            statusText: 'Applied',
            statusDetail: 'The edited version was accepted.',
            bodyText: 'The accepted version is now the active draft.',
          },
          activeChatProjectId,
        );
      }

      clearPreviewReview();
      trackStudioEvent('studio_preview_review_decision', {
        result: 'success',
        decision: 'accept',
        draftId: previewReview.draftId,
        version: previewReview.version,
        sourceMessageId: previewReview.sourceMessageId,
      });
      toast.success('Edited version accepted', {
        id: DRAFT_REVIEW_RESOLUTION_TOAST_ID,
      });
    } catch (err) {
      trackStudioEvent('studio_preview_review_decision', {
        result: 'failure',
        decision: 'accept',
        draftId: previewReview.draftId,
        version: previewReview.version,
        sourceMessageId: previewReview.sourceMessageId,
        errorCode: getTrackingErrorCode(err),
      });
      toast.error((err as { message?: string })?.message || 'Failed to accept the edited version.');
    } finally {
      setIsReviewDecisionPending(false);
    }
  };

  const handleKeepCurrentPreviewVersion = async () => {
    if (!previewReview.active) return;

    setIsReviewDecisionPending(true);
    try {
      syncDefaultPreviewScrollPosition('before');

      if (previewReview.draftId) {
        await projectApi.updateDraftStatus({
          draftId: previewReview.draftId,
          draftStatus: 'ABANDONED',
        });
        rememberRecentDraftStatus(previewReview.draftId, 'ABANDONED');
      }

      let latestConfirmedDraft = null;
      if (currentId) {
        const userProjectId = Number(currentId);

        if (previewReview.draftId) {
          for (let attempt = 0; attempt < 4; attempt += 1) {
            const latestPendingDraft = await projectApi.getLatestDraft(userProjectId, ['PENDING']);
            if (
              !latestPendingDraft ||
              String(latestPendingDraft.id) !== String(previewReview.draftId)
            ) {
              break;
            }
            await waitForDraftSync(150);
          }
        }

        try {
          const { editableDraft } = await resolveEditableDraft(userProjectId);
          latestConfirmedDraft = editableDraft;

          if (editableDraft?.draftStatus === 'CONFIRMED' && editableDraft.content) {
            const restoredConfirmedDraft = await saveConfirmedDraftSnapshot(
              userProjectId,
              editableDraft.content,
              'Kept current version after review.',
              {
                suppressErrorToast: true,
                retries: 4,
                minBaseVersion: previewReview.version ?? editableDraft.version,
              },
            );

            for (let attempt = 0; attempt < 4; attempt += 1) {
              const syncedLatestDraft = await projectApi.getLatestDraft(userProjectId);
              if (
                syncedLatestDraft &&
                String(syncedLatestDraft.id) === String(restoredConfirmedDraft.id) &&
                syncedLatestDraft.draftStatus === 'CONFIRMED'
              ) {
                break;
              }
              await waitForDraftSync(150);
            }

            rememberRecentDraftStatus(restoredConfirmedDraft.id, 'CONFIRMED');
            latestConfirmedDraft = restoredConfirmedDraft;
          }
        } catch (error) {
          console.warn(
            '[StudioPreview] Failed to refresh confirmed draft after keeping current:',
            error,
          );
        }
      }

      setGeneratedHtml(previewReview.beforeHtml);
      if (latestConfirmedDraft) {
        setCurrentDraftMeta(
          latestConfirmedDraft.id,
          latestConfirmedDraft.version,
          latestConfirmedDraft.draftStatus,
        );
      }

      if (previewReview.sourceMessageId) {
        updateChatMessage(
          previewReview.sourceMessageId,
          {
            status: 'dismissed',
            statusText: 'Kept current',
            statusDetail: 'You kept the current draft version.',
            bodyText: 'The proposed edited version was discarded after review.',
          },
          activeChatProjectId,
        );
      }

      clearPreviewReview();
      trackStudioEvent('studio_preview_review_decision', {
        result: 'success',
        decision: 'keep_current',
        draftId: previewReview.draftId,
        version: previewReview.version,
        sourceMessageId: previewReview.sourceMessageId,
      });
      toast.success('Kept current version', {
        id: DRAFT_REVIEW_RESOLUTION_TOAST_ID,
      });
    } catch (err) {
      trackStudioEvent('studio_preview_review_decision', {
        result: 'failure',
        decision: 'keep_current',
        draftId: previewReview.draftId,
        version: previewReview.version,
        sourceMessageId: previewReview.sourceMessageId,
        errorCode: getTrackingErrorCode(err),
      });
      toast.error((err as { message?: string })?.message || 'Failed to keep the current version.');
    } finally {
      setIsReviewDecisionPending(false);
    }
  };

  return (
    <div className="studio-preview-shell animate-in fade-in relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#EDEEF6] duration-500">
      <div className={cn('preview-bar', previewReview.active && 'review-active')}>
        <div className="preview-bar-default">
          <div className="preview-url">
            <Icon
              icon={previewUrlLoading ? 'fa6-solid:spinner' : 'fa6-solid:lock'}
              className={cn('text-[10px]', previewUrlLoading && 'animate-spin')}
            />
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                previewUrlLoading && 'text-[#0D0D1A]/[0.42]',
              )}
            >
              {displayUrl}
            </span>
            <span className="h-4 w-px bg-[#0D0D1A]/10" aria-hidden="true" />
            <button
              type="button"
              className={cn(
                'shrink-0 pl-3 text-[11px] transition-colors',
                canOpenPreview
                  ? 'cursor-pointer text-[#0D0D1A]/[0.38] hover:text-[#0D0D1A]/60'
                  : 'cursor-not-allowed text-[#0D0D1A]/[0.2]',
              )}
              onClick={() => {
                if (!canOpenPreview) return;
                window.open(previewUrl, '_blank', 'noopener,noreferrer');
              }}
              disabled={!canOpenPreview}
            >
              {t('preview.title')}
            </button>
          </div>
          <button
            type="button"
            className={cn(
              'px-2 text-[11px] transition-colors',
              canOpenPublishedView
                ? 'cursor-pointer text-[#0D0D1A]/[0.38] hover:text-[#0D0D1A]/60'
                : 'cursor-not-allowed text-[#0D0D1A]/[0.2]',
            )}
            onClick={() => {
              if (!canOpenPublishedView) return;
              trackEvent({
                eventCode: 'app_edit_site_preview',
                eventData: JSON.stringify({ projectId: currentProjectId }),
              });
              window.open(publishedViewUrl, '_blank', 'noopener,noreferrer');
            }}
            disabled={!canOpenPublishedView}
          >
            view
          </button>
        </div>

        <div className="preview-review-bar">
          <div className="preview-review-copy">
            <div className="preview-review-title">
              {previewReview.title || 'Review latest change'}
            </div>
            <div className="preview-review-note">
              {previewReview.note ||
                'Switch between the current draft and the proposed edit, then decide which version to keep.'}
            </div>
          </div>
          <div className="preview-review-toggle" role="tablist" aria-label="Preview compare">
            <button
              type="button"
              className={cn(
                'preview-review-toggle-btn',
                previewReview.view === 'before' && 'active',
              )}
              onClick={() => {
                preparePreviewScrollRestore('before');
                setPreviewReviewView('before');
              }}
              disabled={isReviewDecisionPending}
            >
              Before
            </button>
            <button
              type="button"
              className={cn(
                'preview-review-toggle-btn',
                previewReview.view === 'after' && 'active',
              )}
              onClick={() => {
                preparePreviewScrollRestore('after');
                setPreviewReviewView('after');
              }}
              disabled={isReviewDecisionPending}
            >
              After
            </button>
          </div>
          <div className="preview-review-actions">
            <button
              type="button"
              className="preview-review-btn keep"
              onClick={handleKeepCurrentPreviewVersion}
              disabled={isReviewDecisionPending}
            >
              Keep current
            </button>
            <button
              type="button"
              className="preview-review-btn accept"
              onClick={handleAcceptPreviewVersion}
              disabled={isReviewDecisionPending}
            >
              Accept
            </button>
          </div>
        </div>
      </div>

      <div className="preview-scroll flex flex-1 justify-center overflow-auto bg-[#EDEEF6] p-6">
        <div
          className={cn(
            'preview-frame-shell relative w-full max-w-[1200px] overflow-hidden rounded-lg bg-white shadow-2xl transition-all duration-300 ease-in-out',
            previewDevice === 'mobile' &&
              'max-w-[390px] rounded-[32px] border-[8px] border-slate-900 shadow-slate-900/10',
            previewDevice === 'tablet' && 'max-w-[768px] rounded-xl',
          )}
          style={{ minHeight: '800px' }}
        >
          {renderPreviewContent()}

          {isPreviewFrameLoading && !shouldShowPreviewRecovery && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white">
              <div className="grid justify-items-center gap-3 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(109,40,217,0.16)] bg-[rgba(109,40,217,0.08)] text-[#6D28D9]">
                  <Icon icon="fa6-solid:spinner" className="h-4 w-4 animate-spin" />
                </div>
                <div className="text-[13px] font-bold text-[#0D0D1A]">Loading preview</div>
              </div>
            </div>
          )}

          {previewDevice === 'mobile' && (
            <div className="absolute bottom-2 left-1/2 h-1.5 w-32 -translate-x-1/2 rounded-full bg-slate-900/20" />
          )}
        </div>
      </div>

      {(isPreviewApplying || isEditing) && (
        <div className="animate-in fade-in pointer-events-auto absolute top-[68px] right-6 bottom-6 left-6 z-40 flex items-center justify-center rounded-[18px] bg-gradient-to-b from-[rgba(244,245,252,0.62)] to-[rgba(235,236,244,0.82)] backdrop-blur-[4px] duration-200">
          <div className="grid w-full max-w-[360px] justify-items-center gap-[10px] rounded-[18px] border border-[rgba(109,40,217,0.12)] bg-[rgba(255,255,255,0.92)] p-[20px_22px] text-center shadow-[0_18px_42px_rgba(76,29,149,0.14),0_6px_16px_rgba(15,23,42,0.08)]">
            <div className="inline-flex items-center gap-[6px] rounded-full bg-[rgba(109,40,217,0.08)] px-[10px] py-[5px] text-[11px] font-bold tracking-[0.04em] text-[#6D28D9] uppercase">
              <i className="fa fa-wand-magic-sparkles text-[13px]"></i>
              {t('expert.review.ai_task_badge')}
            </div>
            <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] text-white shadow-[0_10px_26px_rgba(109,40,217,0.24)]">
              <i className="fa fa-spinner fa-spin" />
            </div>
            <div className="text-[17px] font-bold tracking-[-0.01em] text-[#0D0D1A]">
              {t('expert.review.ai_task_title')}
            </div>
            <div className="max-w-[30ch] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
              {t('expert.review.ai_task_copy')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
